import build_param from "../build_params.json";

const frontendPath = build_param.frontendPath;

/**
 * Shared logout function that handles all cleanup operations
 * @param {Function} auth0Logout - The Auth0 logout function
 * @returns {Promise<void>}
 */
export const performLogout = async (auth0Logout) => {
    try {
        // 1. Initiate Auth0 logout first
        const logoutPromise = auth0Logout({
            logoutParams: {
                returnTo: `${frontendPath}/`,
            },
        });

        // 2. Clear storage
        try {
            localStorage.clear();

            sessionStorage.clear();
        } catch (e) {
            console.error("❌ Error clearing storage:", e);
        }

        // 3. Handle service worker cleanup
        try {
            if ("serviceWorker" in navigator && navigator.serviceWorker) {
                try {
                    const registrations =
                        await navigator.serviceWorker.getRegistrations();
                    if (registrations && registrations.length > 0) {
                        // Set a timeout to prevent hanging
                        const timeout = new Promise((_, reject) =>
                            setTimeout(
                                () =>
                                    reject(
                                        new Error(
                                            "Service worker unregister timeout"
                                        )
                                    ),
                                2000
                            )
                        );

                        await Promise.race([
                            Promise.all(
                                registrations.map(async (registration) => {
                                    if (
                                        registration &&
                                        typeof registration.unregister ===
                                            "function"
                                    ) {
                                        try {
                                            console.log(
                                                "🗑️ Unregistering service worker:",
                                                registration.scope
                                            );
                                            const success =
                                                await registration.unregister();
                                            console.log(
                                                success
                                                    ? "✅ Service worker unregistered"
                                                    : "❌ Failed to unregister service worker"
                                            );
                                        } catch (e) {
                                            console.error(
                                                "❌ Error unregistering service worker:",
                                                e
                                            );
                                        }
                                    }
                                })
                            ),
                            timeout,
                        ]);
                    } else {
                        console.log("ℹ️ No active service workers found");
                    }
                } catch (e) {
                    console.error(
                        "❌ Error accessing service worker registrations:",
                        e
                    );
                }
            } else {
                console.log("ℹ️ No service worker support in this browser");
            }
        } catch (e) {
            console.error("❌ Error during service worker cleanup:", e);
        }

        // 4. Clear cookies
        try {
            const cookies = document.cookie.split(";");
            cookies.forEach((cookie) => {
                const eqPos = cookie.indexOf("=");
                const name =
                    eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                document.cookie =
                    name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                document.cookie =
                    name +
                    "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
                    window.location.hostname;
            });
        } catch (error) {
            console.error("❌ Failed to clear cookies:", error);
        }

        // 5. Clear caches
        if ("caches" in window) {
            try {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map((name) => caches.delete(name))
                );
            } catch (error) {
                console.error("❌ Failed to clear cache storage:", error);
            }
        }

        // 6. Wait for Auth0 logout to complete
        await logoutPromise;
    } catch (error) {
        console.error("❌ Error during logout process:", error);
        // If everything fails, force the redirect
        window.location.href = `${frontendPath}/`;
    }
};
